import React, { useEffect, useRef } from "react";
import { createGlobalStyle } from "styled-components";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Onboarding from "./Pages/OnboardingPage.js";
import Main from "./Pages/MainPage.js";
import Query from "./Pages/QueryPage.js";
import Test from "./Pages/TestPage.js";
import TermsOfService from "./Pages/TermsOfService.js";
import PrivacyPolicy from "./Pages/PrivacyPolicy.js";
import InvitePage from "./Pages/InvitePage.js";
import AppGuidePage from "./Pages/AppGuidePage.js";
import LabsPage from "./Pages/LabsCatalogPage.js";
import LabServiceRoute from "./Components/LabServiceRoute.jsx";
import LabServicePendingPage from "./Pages/LabServicePendingPage.js";
import { LAB_SERVICES } from "./labs/services.js";
import { consumeRehearsalReturn, REHEARSAL_RETURN_KEY } from "./utils/rehearsal.js";
import { consumeEvidenceReturn, EVIDENCE_RETURN_KEY } from "./utils/evidenceTimeline.js";
import KakaoRedirect from "./Components/KakaoRedirect.jsx";
import GoogleRedirect from "./Components/GoogleRedirect.jsx";
import NaverRedirect from "./Components/NaverRedirect.jsx";
import AppleRedirect from "./Components/AppleRedirect.jsx";
import { Helmet, HelmetProvider } from "react-helmet-async";
import useStore from "./store.js";
import Cookies from "js-cookie";
import { trackNaverPageView } from "./utils/naverConversion.js";
import { trackRecentSignup } from "./utils/signupTracking.js";
import PendingQueryMonitor from "./Components/PendingQueryMonitor.jsx";
import PaymentRedirectHandler from "./Components/PaymentRedirectHandler.jsx";
import ChatNavigationProvider from "./Components/ChatNavigationProvider.jsx";
import ModalLowActionInProgress from "./Components/Modal/ModalLowActionInProgress.jsx";
import VisualRegressionPage from "./Pages/VisualRegressionPage.jsx";
import PaymentVisualRegressionPage from "./Pages/PaymentVisualRegressionPage.jsx";
import {
  captureSignupAttribution,
  clearSignupAttribution,
} from "./utils/attribution.js";
import { consumeLandingExampleAfterLogin, landingExampleSearch } from "./utils/landingExample.js";
import { observeMailVisit } from "./utils/mailAttribution.js";
import { startBehaviorAnalytics } from "./utils/behaviorAnalytics.js";
import config from "./config.js";

const LabsPreviewPage = process.env.NODE_ENV !== "production" ? require("./Pages/LabsPreviewPage.js").default : null;

// import "./App.css";

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackLogin, lowActionConflict, clearLowActionConflict, updateUserInfo, updateUserLaw } = useStore();
  const hasTrackedLogin = useRef(false);
  const lastTrackedNaverPath = useRef("");

  useEffect(() => startBehaviorAnalytics(config.serverUri), [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/main") return;
    const pending = consumeLandingExampleAfterLogin();
    if (pending && !landingExampleSearch(location.search)) {
      navigate({ pathname: "/main", search: pending }, { replace: true, state: location.state });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!["/main", "/app-guide"].includes(location.pathname)) return;
    const evidenceAt = Number(sessionStorage.getItem(EVIDENCE_RETURN_KEY));
    const rehearsalAt = Number(sessionStorage.getItem(REHEARSAL_RETURN_KEY));
    if (!evidenceAt && !rehearsalAt) return;
    let active = true;
    updateUserInfo(() => useStore.setState({ userInfo: null })).then(() => {
      if (!active) return;
      const loggedIn = Boolean(useStore.getState().userInfo);
      const evidence = consumeEvidenceReturn(location.pathname, loggedIn);
      const rehearsal = consumeRehearsalReturn(location.pathname, loggedIn);
      const target = evidence && (!rehearsal || evidenceAt >= rehearsalAt)
        ? "/lab/evidence" : rehearsal ? "/lab/rehearsal" : null;
      if (target) {
        const userId = useStore.getState().userInfo?.id;
        updateUserLaw().then((credits) => {
          const current = useStore.getState();
          if (userId != null && current.userInfo?.id === userId) trackRecentSignup(credits);
        }).catch(() => {});
        navigate({ pathname: target, search: location.search }, { replace: true });
      }
    });
    return () => { active = false; };
  }, [location.pathname, location.search, navigate, updateUserInfo, updateUserLaw]);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    // PWA 첫 실행의 온보딩 이동에서도 외부에서 연 랩스 링크는 유지한다.
    if (
      window.matchMedia("(display-mode: standalone)").matches &&
      !sessionStorage.getItem("redirected")
    ) {
      sessionStorage.setItem("redirected", "true");
      const path = window.location.pathname.replace(/\/$/, "");
      if (path !== "/lab" && path !== "/labs" && !path.startsWith("/lab/")) navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    const { pathname } = location;
    const match = pathname.match(/^\/main\/q\/(\d+|waiting)$/);
    // 결과페이지에 숫자나 'waiting'이 아닌 다른 값이 오면 /main으로 리디렉션
    if (pathname.startsWith("/main/q/") && !match) {
      navigate("/main");
    }
  }, [location, navigate]);

  useEffect(() => {
    captureSignupAttribution();
    return observeMailVisit();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (Cookies.get("lawlow")) {
      clearSignupAttribution();
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;

    if (lastTrackedNaverPath.current === currentPath) return;

    lastTrackedNaverPath.current = currentPath;
    trackNaverPageView();
  }, [location.pathname, location.search]);

  // 전역 trackLogin 처리: 로그인된 상태로 보호된 페이지 접속 시 카운팅
  useEffect(() => {
    const { pathname } = location;

    // 보호된 페이지 목록 (로그인 필요한 페이지들)
    const protectedRoutes = ['/main', '/main/q'];
    const isProtectedRoute = protectedRoutes.some(route => pathname.startsWith(route));

    // OAuth redirect 페이지는 제외 (이미 navigate로 메인 페이지로 이동함)
    const isOAuthRoute = pathname.startsWith('/oauth/');

    if (isProtectedRoute && !isOAuthRoute) {
      const loginInfo = Cookies.get("lawlow");
      const hasCountedThisSession = sessionStorage.getItem("loginCountedThisSession");

      if (loginInfo && !hasCountedThisSession && !hasTrackedLogin.current) {
        hasTrackedLogin.current = true;
        trackLogin().then(() => {
          sessionStorage.setItem("loginCountedThisSession", "true");
        });
      }
    }
  }, [location, trackLogin]);

  return (
    <div className="App">
      <GlobalStyle />
      <HelmetProvider>
        <Helmet>
          <meta name="description" content="로우로우(Lawlow) | 990원짜리 나만의 법조인, 당신의 상황에 맞는 판례를 찾아드립니다." />
          <meta name="robots" content={process.env.REACT_APP_ENV === 'production' ? 'index, follow' : 'noindex, nofollow'} />
          <meta name="googlebot" content={process.env.REACT_APP_ENV === 'production' ? 'index, follow' : 'noindex, nofollow'} />
        </Helmet>

        <ChatNavigationProvider>
          <PendingQueryMonitor />
          <PaymentRedirectHandler />
          <ModalLowActionInProgress
            conflict={lowActionConflict}
            onClose={clearLowActionConflict}
          />
          <Routes>
            <Route path="/" element={<Onboarding />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/app-guide" element={<AppGuidePage />} />
            <Route path="/lab" element={<LabsPage />} />
            <Route path="/labs" element={<Navigate to={{ pathname: "/lab", search: location.search, hash: location.hash }} replace />} />
            {LAB_SERVICES.filter((service) => service.status === "archived" || (service.status === "published" && Date.parse(service.publishedAt) <= Date.now())).map((service) => <Route key={service.id} path={service.path} element={<LabServiceRoute service={service} />} />)}
            <Route path="/lab/*" element={<LabServicePendingPage />} />
            <Route path="/main" element={<Main />} />
            <Route path="/main/q/:status" element={<Query />} />
            <Route path="/test" element={<Test />} />
            {process.env.NODE_ENV !== "production" && (
              <>
                <Route path="/__visual/guest-chat-controls" element={<VisualRegressionPage />} />
                <Route path="/__visual/payment-products" element={<PaymentVisualRegressionPage />} />
                <Route path="/__visual/labs-services" element={<LabsPreviewPage />} />
              </>
            )}
            <Route path="/oauth/kakao" element={<KakaoRedirect />} />
            <Route path="/oauth/google" element={<GoogleRedirect />} />
            <Route path="/oauth/naver" element={<NaverRedirect />} />
            <Route path="/oauth/apple" element={<AppleRedirect />} />
            <Route path="/termsofservice" element={<TermsOfService />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="*" element={<Navigate to="/main" />} />
            {/* 잘못된 URL 입력 시 메인으로 돌아오도록 처리 */}
          </Routes>
        </ChatNavigationProvider>
      </HelmetProvider>
      {/* </BrowserRouter> */}
    </div>
  );
}

const GlobalStyle = createGlobalStyle`
body {
  font-family: 'Pretendard', sans-serif;
}
* {
  font-family: inherit;
}
`;

export default App;
